import { Paper, SxProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  borderLight,
  grayDefault,
  grey9,
  white,
  yellow,
} from 'app/components/ThemeProvider/colors';
import { getTheme } from 'app/components/ThemeProvider/theme';

const theme = getTheme();

export const notificationOverlay: SxProps = {
  position: 'fixed',
  top: '80px',
  right: 0,
  height: '87vh',
  width: { xs: '100vw', sm: 480 },
  bgcolor: 'white',
  zIndex: 1300,
  boxShadow: 6,
  display: 'flex',
  flexDirection: 'column',
  borderLeft: `1px solid ${grayDefault}`,

  // Mobile: opens as a left-anchored, full-height drawer (like the sidebar).
  // The panel itself does not scroll — only the list below does.
  [theme.breakpoints.down('md')]: {
    top: 0,
    left: 0,
    right: 'auto',
    height: '100%',
    width: '341px',
    maxWidth: '100%',
    overflow: 'hidden',
    borderLeft: 'none',
    borderRight: `1px solid ${borderLight}`,
    boxShadow: '0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 8px 10px -6px rgba(0, 0, 0, 0.1)',
  },
};

// Left backdrop, mobile only — mirrors the sidebar drawer overlay
export const notificationBackdrop: SxProps = {
  display: 'none',

  [theme.breakpoints.down('md')]: {
    display: 'block',
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.15)',
    zIndex: 1299,
  },
};

// Mobile-only drawer header: logo on the left, close button on the right
export const notificationMobileHeader: SxProps = {
  display: 'none',

  [theme.breakpoints.down('md')]: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flex: 'none',
    width: '100%',
    height: '68px',
    p: '12px 16px',
    background: white,
    borderBottom: `1px solid ${borderLight}`,
  },
};

export const notificationLogo: SxProps = {
  width: '94px',
  height: '35px',
};

export const notificationCloseButton: SxProps = {
  flex: 'none',
  p: '16px',
  borderRadius: '10px',

  '& .MuiSvgIcon-root': {
    width: '14px',
    height: '14px',
  },
};

export const notificationHeader: SxProps = {
  display: 'flex',
  alignItems: 'center',
  p: '24px 32px',
  borderBottom: `1px solid ${grey9}`,
  width: '100%',
  backgroundColor: white,
  zIndex: 9,
  fontSize: 24,
  fontWeight: 600,

  // Mobile: sits in normal flow (not sticky) under the logo/close header
  [theme.breakpoints.down('md')]: {
    position: 'static',
    width: '100%',
    flex: 'none',
    p: '16px 32px',
  },
};

export const notificationTitle: SxProps = {
  fontWeight: 700,
  fontSize: 20,
};

// Desktop: offset the fixed header. Mobile: the only scrollable area.
export const notificationList: SxProps = {
  overflowY: 'auto',
  [theme.breakpoints.down('md')]: {
    flex: 1,
    minHeight: 0,
  },
};

export const notificationDot: SxProps = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: yellow,
  position: 'absolute',
  left: 16,
  top: 32,
};

export const NotificationItem = styled(Paper)<{ isRead?: boolean }>(({ isRead }) => ({
  padding: '16px 32px',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: !isRead ? 'transparent' : 'rgba(20, 25, 31, 0.04)',
  boxShadow: 'none',
  borderRadius: 0,
  position: 'relative',
  cursor: 'pointer',
}));
